import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import bannersModule, { BannerTypes } from '@gin-betting/banners';
import core from '@gin-betting/core';
import { find, get } from 'lodash';
import { createStructuredSelector } from 'reselect';

import { ACTIVE_MARKET_STATUSES } from 'app-constants';
import { useMemoSelector } from 'hooks';

interface Input {
    zoneId: string;
    withSportEvent?: boolean;
}

interface Output {
    interval: number;
    banners: BannerTypes.BannerItem[];
}

const DEFAULT_INTERVAL = 5 * 1000;

const {
    selectors: {
        makeSelectBannerByZoneId,
        makeBannerZoneSettingsSelector,
        makeSelectMissingBannerZoneIds,
    },
    actions: { getBannersData },
} = bannersModule;

const {
    utils: { getBettingOptions },
} = core;

function useBanners({ zoneId, withSportEvent = true }: Input): Output {
    const dispatch = useDispatch();

    const {
        bannerZone,
        zoneSettings,
        missingZoneIds,
    } = useMemoSelector(makeSelectProps(zoneId), {}, [zoneId]);

    const intervalPair = find(zoneSettings, { name: 'interval' });
    const interval = +(intervalPair?.value ?? DEFAULT_INTERVAL);

    const banners = bannerZone?.config ?? [];
    const missingZoneId = get(missingZoneIds, 0, undefined);

    useEffect(() => {
        if (!missingZoneId) return;

        dispatch(
            getBannersData({
                zoneId: missingZoneId,
                marketStatuses: ACTIVE_MARKET_STATUSES,
                marketLimit: 1,
                sportIds: getBettingOptions().sportIds,
                withSportEvent,
            })
        );
    }, [dispatch, missingZoneId, withSportEvent]);

    return {
        interval,
        banners,
    };
}

const makeSelectProps = (zoneId: string) =>
    createStructuredSelector({
        zoneSettings: makeBannerZoneSettingsSelector(zoneId),
        bannerZone: makeSelectBannerByZoneId(zoneId),
        missingZoneIds: makeSelectMissingBannerZoneIds([zoneId]),
    });

export default useBanners;
